/**
 * Auto-Progress System
 * 
 * Automatically adjusts weights for the next session based on:
 * - RPE trends (too easy = increase, too hard = decrease)
 * - Rep completion (hit all reps = progress)
 * - Historical performance patterns
 * 
 * This runs AFTER each session and prepares adjustments for next time.
 */

import { getExerciseHistory, type ExerciseLog } from './sessionStorage';
import { getSessionHistory, type PTSessionLog, type SessionLog } from './sessionStorage';
import { getCurrentUserId } from './anonymousAuth';
import { db } from './db';
import {
  getExerciseMetadata,
  isCompoundMovement,
  getExerciseLevel
} from './exerciseDatabase';
import { applyDoubleProgression } from './doubleProgression';

type ExerciseLevel = 'beginner' | 'intermediate' | 'expert';

// ============================================================================
// TYPES
// ============================================================================

export interface ProgressionDecision {
  exerciseName: string;
  currentWeight: number;
  suggestedWeight: number;
  action: 'increase' | 'decrease' | 'maintain';
  percentage: number;
  reason: string;
  confidence: 'high' | 'medium' | 'low';
  dataPoints: number;
}

export interface AutoProgressResult {
  progressions: ProgressionDecision[];
  summary: string;
  totalExercises: number;
  increased: number;
  decreased: number;
  maintained: number;
}

// Storage key removed - now using Dexie

// ============================================================================
// MAIN FUNCTION: Analyze Session and Generate Progressions
// ============================================================================

/**
 * Analyze a completed session and generate progression decisions
 * Called after each workout is saved
 */
export async function analyzeSessionForProgression(
  session: PTSessionLog
): Promise<AutoProgressResult> {
  const progressions: ProgressionDecision[] = [];

  // Skip progression analysis for deload sessions — reduced weights/RPE would
  // produce misleading "ready to increase" signals
  if ((session as any).isDeload) {
    console.log('⏭ Auto-Progress: Skipping deload session (would corrupt progression data)');
    return {
      progressions: [],
      summary: 'Deload session — progression analysis skipped.',
      totalExercises: 0,
      increased: 0,
      decreased: 0,
      maintained: 0,
    };
  }

  console.log('� Auto-Progress: Analyzing session for progressions...');

  for (const exercise of session.exercises) {
    if (!exercise.sets || exercise.sets.length === 0) continue;
    
    const decision = await analyzeExerciseProgression(exercise);
    if (decision) {
      progressions.push(decision);
    }
  }
  
  // Count actions
  const increased = progressions.filter(p => p.action === 'increase').length;
  const decreased = progressions.filter(p => p.action === 'decrease').length;
  const maintained = progressions.filter(p => p.action === 'maintain').length;
  
  // Save progressions for next session
  await saveProgressionDecisions(progressions);
  
  // Generate summary
  let summary = '';
  if (increased > 0) {
    summary += `${increased} exercise${increased > 1 ? 's' : ''} will progress. `;
  }
  if (decreased > 0) {
    summary += `${decreased} exercise${decreased > 1 ? 's' : ''} will reduce. `;
  }
  if (maintained > 0 && increased === 0 && decreased === 0) {
    summary = 'All exercises maintaining current weights.';
  }
  
  console.log(` Auto-Progress: ${increased} increase, ${decreased} decrease, ${maintained} maintain`);
  
  return {
    progressions,
    summary: summary.trim() || 'No progression changes needed.',
    totalExercises: progressions.length,
    increased,
    decreased,
    maintained,
  };
}

// ============================================================================
// ANALYZE INDIVIDUAL EXERCISE
// ============================================================================

async function analyzeExerciseProgression(
  exerciseLog: ExerciseLog
): Promise<ProgressionDecision | null> {
  const exerciseName = exerciseLog.exerciseName;
  
  // Get historical data for this exercise
  const history = await getExerciseHistory(exerciseName);
  
  if (history.length < 2) {
    return null; // Need at least 2 sessions to make a decision
  }
  
  // Get exercise metadata for context-aware progression
  const metadata = getExerciseMetadata(exerciseName);
  const exerciseLevel = metadata?.level || getExerciseLevel(exerciseName);
  const mechanic = metadata?.mechanic || (isCompoundMovement(exerciseName) ? 'compound' : 'isolation');
  
  // Define progression magnitudes based on metadata
  const getProgressionMagnitudes = (level: ExerciseLevel, isCompound: boolean) => {
    // Level-based progression curves
    const levelFactors = {
      'beginner': { small: 2.5, large: 5 },     // Gentler for beginners
      'intermediate': { small: 5, large: 10 },  // Standard progression
      'expert': { small: 2.5, large: 5 }        // Micro-progressions for advanced
    };
    
    // Mechanic-based adjustments
    const mechanicFactor = isCompound ? 0.85 : 1.0; // 15% more conservative for compounds
    
    const levelFactor = levelFactors[level];
    
    return {
      small: levelFactor.small * mechanicFactor,
      large: levelFactor.large * mechanicFactor,
      decrease: isCompound ? -7.5 : -5, // Larger decrease for compounds
    };
  };
  
  const progressionMags = getProgressionMagnitudes(exerciseLevel, mechanic === 'compound');
  
  // Get current session data. Filter to 'normal' sets only — drop sets,
  // AMRAPs, rest-pause clusters and warmups all distort the RPE / weight
  // signal that progression decisions rely on (a drop set at 70% with
  // high RPE looks like the user is "ready to deload" when actually
  // they just hit a heavy top set immediately before).
  const isNormalSet = (s: { kind?: string }) => !s.kind || s.kind === 'normal';
  const completedSets = exerciseLog.sets?.filter(s => isNormalSet(s) && s.weight > 0 && s.reps > 0) || [];
  if (completedSets.length === 0) return null;

  const currentWeight = completedSets[0].weight;
  const rawAvgRPE = calculateAvgRPE(completedSets);
  const allRepsCompleted = checkAllRepsCompleted(exerciseLog);

  // Get RPE trend from history
  const rpeTrend = calculateRPETrend(history.slice(-5)); // Last 5 sessions

  // Apply RPE calibration if available (Fix 6)
  const calibration = await getRPECalibrationFactor();
  let avgRPE = rawAvgRPE !== null ? rawAvgRPE * calibration : null;
  if (rawAvgRPE !== null && calibration !== 1.0) {
    console.log(`  RPE Calibration: ${exerciseName} | raw ${rawAvgRPE.toFixed(1)} × ${calibration.toFixed(2)} = ${avgRPE!.toFixed(1)}`);
  }

  // Apply HR-derived per-exercise calibration on top. This is an
  // ADDITIVE offset (in RPE units) learned from how much the user's
  // reported RPE diverges from HR-predicted RPE on this specific lift.
  // Chronic under-reporters (say "RPE 7" while HR is screaming RPE 9)
  // get their effective avgRPE pushed UP — which keeps load progression
  // honest (a real RPE 9 set isn't ready for a +5lb bump). Over-reporters
  // get the opposite. No-op for the first 5 samples per exercise; clamps
  // ±2.0 to prevent runaway corrections. See lib/hrConsensus.ts.
  if (avgRPE !== null) {
    try {
      const { loadCalibrationFactor, applyCalibration } = await import('./hrConsensus');
      const userId = getCurrentUserId();
      if (userId) {
        const factor = loadCalibrationFactor(userId, exerciseName);
        const hrAdjusted = applyCalibration(avgRPE, factor);
        if (factor && hrAdjusted !== avgRPE) {
          console.log(`  HR-RPE Calibration: ${exerciseName} | ${avgRPE.toFixed(1)} → ${hrAdjusted.toFixed(1)} (factor ${factor.factor.toFixed(2)}, n=${factor.sampleSize})`);
          avgRPE = hrAdjusted;
        }
      }
    } catch (e) {
      console.warn('[hr-calibration] apply failed (non-critical):', e);
    }
  }

  // Decision logic
  let action: 'increase' | 'decrease' | 'maintain' = 'maintain';
  let percentage = 0;
  let reason = '';
  let confidence: 'high' | 'medium' | 'low' = 'medium';

  // ===== DOUBLE PROGRESSION (Fix 2) — runs BEFORE RPE rules =====
  let doubleProgApplied = false;
  if (avgRPE !== null || allRepsCompleted) {
    try {
      const completedRepsPerSet = completedSets.map(s => s.reps);
      // Parse target reps from targetSets (e.g., "3x10" -> 10)
      const targetSetsStr = exerciseLog.targetSets || '';
      const repsMatch = targetSetsStr.match(/\d+\s*[x×]\s*(\d+)/i);
      const targetReps = repsMatch ? parseInt(repsMatch[1]) : 10;

      const dpResult = await applyDoubleProgression(
        exerciseName,
        currentWeight,
        targetReps,
        completedRepsPerSet,
        avgRPE ?? 7.5 // conservative default only for double progression evaluation
      );
      if (dpResult.applied) {
        doubleProgApplied = true;
        if (dpResult.weight !== currentWeight) {
          action = 'increase';
          percentage = currentWeight > 0 ? ((dpResult.weight - currentWeight) / currentWeight) * 100 : 0;
        } else {
          action = 'maintain'; // rep increase, not weight change
          percentage = 0;
        }
        reason = `[Double Progression] ${dpResult.reason}`;
        confidence = avgRPE !== null ? 'high' : 'medium';
        console.log(`  Double Progression: ${exerciseName} | ${dpResult.reason}`);
      }
    } catch (error) {
      console.log(`  Double progression failed for ${exerciseName}:`, error);
    }
  }

  // ===== METADATA-AWARE PROGRESSION RULES =====
  // When no RPE data exists, skip all RPE-dependent rules (Fix 1)
  // When double progression fired an increase, skip increase rules but keep decrease rules

  if (avgRPE === null && !doubleProgApplied) {
    // No RPE data and double progression didn't fire — only allow rep-based fallback
    if (!allRepsCompleted) {
      action = 'decrease';
      percentage = progressionMags.decrease;
      reason = `Failed reps on ${exerciseLevel} ${mechanic} (no RPE data)`;
      confidence = 'low';
    } else {
      action = 'maintain';
      reason = `No RPE data — maintaining weight until RPE is logged`;
      confidence = 'low';
    }
    console.log(`  No RPE data for ${exerciseName} — skipping RPE-dependent rules`);
  } else if (avgRPE !== null && !doubleProgApplied) {
    // Full RPE-aware rule chain (original rules, only when double progression didn't increase)
    // Rule 1: RPE consistently low (≤7) + all reps completed = INCREASE
    if (avgRPE <= 7 && allRepsCompleted) {
      action = 'increase';
      percentage = avgRPE <= 6 ? progressionMags.large : progressionMags.small;
      reason = `RPE ${avgRPE.toFixed(1)} indicates capacity for more weight (${mechanic})`;
      confidence = avgRPE <= 6 ? 'high' : 'medium';
    }
    // Rule 2: RPE consistently high (≥9) = DECREASE
    else if (avgRPE >= 9) {
      action = 'decrease';
      percentage = avgRPE >= 9.5 ? progressionMags.decrease * 1.5 : progressionMags.decrease;
      reason = `RPE ${avgRPE.toFixed(1)} too high for ${mechanic} movement`;
      confidence = 'high';
    }
    // Rule 3: RPE trending down over last few sessions = INCREASE
    else if (rpeTrend === 'decreasing' && avgRPE <= 8 && allRepsCompleted) {
      action = 'increase';
      percentage = progressionMags.small;
      reason = `RPE trending down - adaptation occurring (${exerciseLevel} level)`;
      confidence = 'medium';
    }
    // Rule 4: RPE trending up over last few sessions = Consider decrease
    else if (rpeTrend === 'increasing' && avgRPE >= 8.5) {
      action = 'decrease';
      percentage = progressionMags.decrease * 0.75; // Moderate decrease
      reason = `RPE trending up - ${mechanic} showing fatigue`;
      confidence = 'medium';
    }
    // Rule 5: Failed reps = DECREASE
    else if (!allRepsCompleted) {
      action = 'decrease';
      percentage = progressionMags.decrease;
      reason = `Failed reps on ${exerciseLevel} ${mechanic} - adjusting load`;
      confidence = 'high';
    }
    // Rule 6: Stable RPE 7-8 + all reps = Small increase
    else if (avgRPE >= 7 && avgRPE <= 8 && allRepsCompleted && history.length >= 3) {
      // Check if weight has been same for 3+ sessions
      const recentWeights = history.slice(-3).map(h => h.sets?.[0]?.weight || 0);
      const weightStable = recentWeights.every(w => Math.abs(w - currentWeight) < 5);

      if (weightStable) {
        action = 'increase';
        percentage = progressionMags.small;
        reason = `Weight stable at manageable RPE - ${exerciseLevel} ready to progress`;
        confidence = 'medium';
      }
    }
  } else if (doubleProgApplied && avgRPE !== null) {
    // Double progression fired — only allow decrease overrides (Rules 2, 4, 5)
    if (avgRPE >= 9) {
      action = 'decrease';
      percentage = avgRPE >= 9.5 ? progressionMags.decrease * 1.5 : progressionMags.decrease;
      reason = `RPE ${avgRPE.toFixed(1)} too high for ${mechanic} movement (overrides double progression)`;
      confidence = 'high';
    } else if (rpeTrend === 'increasing' && avgRPE >= 8.5) {
      action = 'decrease';
      percentage = progressionMags.decrease * 0.75;
      reason = `RPE trending up - ${mechanic} showing fatigue (overrides double progression)`;
      confidence = 'medium';
    } else if (!allRepsCompleted) {
      action = 'decrease';
      percentage = progressionMags.decrease;
      reason = `Failed reps on ${exerciseLevel} ${mechanic} (overrides double progression)`;
      confidence = 'high';
    }
  }

  // ──────────────────────────────────────────────────────────────────
  // REST-PACE GATE
  // ──────────────────────────────────────────────────────────────────
  // Closes the loop on lib/restPaceLearning.analyzeRestPace. The rest
  // analyzer already detects "rest was the limiter" (short actual rest +
  // high next-set RPE means the user got crushed by lack of recovery,
  // NOT by too much load). Before today, that finding only updated the
  // displayed rest in V2's RESTING screen — the progression engine
  // happily bumped the weight anyway. Now: if rest is the limiter,
  // we OVERRIDE an 'increase' decision back to 'maintain' and surface
  // the rest-bump as the action. Decreases stand — high RPE under any
  // condition still warrants caution.
  if (action === 'increase') {
    try {
      const { analyzeRestPace } = await import('./restPaceLearning');
      // Parse prescribed rest. The progression engine doesn't carry it
      // explicitly; the rest analyzer accepts the parsed seconds and
      // returns 'insufficient-data' when we have nothing useful — safe
      // fallback. Use 120s as a typical compound default if we can't
      // parse from the log.
      const restStr = (exerciseLog as any).restPeriod ?? '';
      const restMatch = /(\d+)\s*(s|sec|m|min)?/i.exec(restStr);
      let prescribedRestSec = 120;
      if (restMatch) {
        const n = parseInt(restMatch[1], 10);
        const unit = (restMatch[2] || 's').toLowerCase();
        prescribedRestSec = (unit.startsWith('m')) ? n * 60 : n;
      }
      const profile = await analyzeRestPace(exerciseName, prescribedRestSec, { minSamples: 3 });
      if (profile.suggestion === 'longer' && profile.reason.includes('limiter')) {
        // Short rest + high RPE — exactly the "rest is the limiter" case.
        // Don't bump the weight; the user will just fail next time too.
        action = 'maintain';
        percentage = 0;
        reason = `[Rest-pace gate] ${profile.reason} — holding weight; intent is more rest before another progression attempt.`;
        confidence = 'high';
        console.log(`  Rest-pace gate: ${exerciseName} — increase blocked, rest is the limiter`);
      }
    } catch (e) {
      // Non-critical — fall through to the original increase decision.
      console.warn('  Rest-pace gate failed (non-critical):', e);
    }
  }

  // Calculate new weight
  const suggestedWeight = Math.round(currentWeight * (1 + percentage / 100));

  // Round to nearest 2.5 lbs
  const roundedWeight = Math.round(suggestedWeight / 2.5) * 2.5;

  return {
    exerciseName,
    currentWeight,
    suggestedWeight: roundedWeight,
    action,
    percentage,
    reason,
    confidence,
    dataPoints: history.length,
  };
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get RPE calibration factor from user's adaptive profile (Fix 6).
 * Conservative reporters get adjusted up, aggressive reporters adjusted down.
 * Returns 1.0 if profile missing or confidence too low.
 */
async function getRPECalibrationFactor(): Promise<number> {
  try {
    const userId = getCurrentUserId();
    if (!userId) return 1.0;

    const { getUserProfile } = await import('./userProfileService');
    const profile = await getUserProfile(userId);
    if (!profile?.rpeCalibration) return 1.0;

    const { calibrationFactor, confidence } = profile.rpeCalibration;
    // Only apply calibration when we have enough confidence
    if (confidence < 0.5) return 1.0;

    // Clamp to safe range (0.85–1.15) to avoid extreme corrections
    const clamped = Math.max(0.85, Math.min(1.15, calibrationFactor));
    console.log(`  RPE Calibration factor: ${clamped.toFixed(2)} (confidence: ${confidence.toFixed(2)}, tendency: ${profile.rpeCalibration.tendency})`);
    return clamped;
  } catch {
    return 1.0;
  }
}

function calculateAvgRPE(sets: Array<{ rpe?: number | null }>): number | null {
  const validRPEs = sets.filter(s => s.rpe && s.rpe > 0).map(s => s.rpe!);
  if (validRPEs.length === 0) return null; // No real RPE data — don't guess
  return validRPEs.reduce((sum, rpe) => sum + rpe, 0) / validRPEs.length;
}

function checkAllRepsCompleted(exerciseLog: ExerciseLog): boolean {
  if (!exerciseLog.sets) return true;
  
  // Parse target reps from targetSets (e.g., "3x10" -> 10)
  const targetSetsStr = exerciseLog.targetSets || '';
  const repsMatch = targetSetsStr.match(/\d+\s*[x×]\s*(\d+)/i);
  const targetReps = repsMatch ? parseInt(repsMatch[1]) : 10;
  
  return exerciseLog.sets.every(set => set.reps >= targetReps); // Must hit ALL prescribed reps to progress
}

function calculateRPETrend(history: ExerciseLog[]): 'increasing' | 'decreasing' | 'stable' {
  // Only use sessions that have real RPE data (filter out sessions with no RPE)
  const rpes: number[] = [];
  for (const h of history) {
    const sets = h.sets || [];
    const validRPEs = sets.filter(s => s.rpe && s.rpe > 0).map(s => s.rpe!);
    if (validRPEs.length > 0) {
      rpes.push(validRPEs.reduce((sum, r) => sum + r, 0) / validRPEs.length);
    }
  }

  // Require 3+ sessions with real RPE data for a meaningful trend
  if (rpes.length < 3) return 'stable';

  // Simple linear trend
  const firstHalf = rpes.slice(0, Math.floor(rpes.length / 2));
  const secondHalf = rpes.slice(Math.floor(rpes.length / 2));

  const firstAvg = firstHalf.reduce((sum, r) => sum + r, 0) / firstHalf.length;
  const secondAvg = secondHalf.reduce((sum, r) => sum + r, 0) / secondHalf.length;

  const diff = secondAvg - firstAvg;

  if (diff > 0.5) return 'increasing';
  if (diff < -0.5) return 'decreasing';
  return 'stable';
}

// ============================================================================
// STORAGE: Save and Load Progression Decisions (Dexie)
// ============================================================================

async function saveProgressionDecisions(progressions: ProgressionDecision[]): Promise<void> {
  try {
    const userId = getCurrentUserId();
    if (!userId) {
      console.warn('No user ID available for saving progression decisions');
      return;
    }

    // Save each progression to Dexie
    for (const p of progressions) {
      const id = `${userId}_${p.exerciseName.toLowerCase()}`;
      await db.autoProgressDecisions.put({
        id,
        userId,
        exerciseName: p.exerciseName,
        currentWeight: p.currentWeight,
        suggestedWeight: p.suggestedWeight,
        action: p.action,
        percentage: p.percentage,
        reason: p.reason,
        confidence: p.confidence,
        dataPoints: p.dataPoints,
        updatedAt: new Date(),
      });
    }

    console.log(` Saved ${progressions.length} progression decisions to Dexie`);
  } catch (error) {
    console.error('Failed to save progression decisions:', error);
  }
}

export async function loadProgressionDecisions(): Promise<ProgressionDecision[]> {
  try {
    const userId = getCurrentUserId();
    if (!userId) return [];

    // Get all decisions for this user
    const decisions = await db.autoProgressDecisions
      .where('userId')
      .equals(userId)
      .toArray();

    // Keep decisions for 30 days (was 7 — too aggressive, users take rest weeks).
    // Decisions are still APPLIED on next workout, then refreshed by that session's analysis.
    // Only truly stale decisions (>30 days) get cleaned up.
    const now = new Date();
    const validDecisions = decisions.filter(d => {
      const daysSinceUpdate = (now.getTime() - d.updatedAt.getTime()) / (1000 * 60 * 60 * 24);
      return daysSinceUpdate <= 30;
    });

    // Clean up very stale decisions (>30 days)
    const staleIds = decisions
      .filter(d => {
        const daysSinceUpdate = (now.getTime() - d.updatedAt.getTime()) / (1000 * 60 * 60 * 24);
        return daysSinceUpdate > 30;
      })
      .map(d => d.id);

    if (staleIds.length > 0) {
      await db.autoProgressDecisions.bulkDelete(staleIds);
      console.log(` Cleaned up ${staleIds.length} stale progression decisions (>30 days)`);
    }

    return validDecisions;
  } catch (error) {
    console.error('Failed to load progression decisions:', error);
    return [];
  }
}

/**
 * Get progression decision for a specific exercise
 * Used by workout generator to apply saved progressions
 */
export async function getExerciseProgression(exerciseName: string): Promise<ProgressionDecision | null> {
  const decisions = await loadProgressionDecisions();
  return decisions.find(d => 
    d.exerciseName.toLowerCase() === exerciseName.toLowerCase()
  ) || null;
}

/**
 * Apply progression to a weight value
 * Returns adjusted weight based on saved progression decision
 */
export async function applyProgression(
  exerciseName: string,
  baseWeight: number
): Promise<{ weight: number; applied: boolean; reason?: string; confidence?: string }> {
  const decision = await getExerciseProgression(exerciseName);

  if (!decision || decision.action === 'maintain') {
    return { weight: baseWeight, applied: false };
  }

  // Apply the progression percentage
  // Round to 5 lb increments (standard gym plates: 2.5 lb on each side = 5 lb total)
  const adjustedWeight = Math.round(baseWeight * (1 + decision.percentage / 100));
  const roundedWeight = Math.round(adjustedWeight / 5) * 5;

  console.log(` Auto-Progress: ${exerciseName} | ${baseWeight} → ${roundedWeight} lbs (${decision.reason})`);

  return {
    weight: roundedWeight,
    applied: true,
    reason: decision.reason,
    confidence: decision.confidence, // Pass through for P0/P1 merge comparison
  };
}

/**
 * Compute the user's recent acceptance rate for intra-session RPE-gate
 * suggestions on a given lift. Reads up to the last `lookback` sessions
 * and counts proposals + accepts.
 *
 * Used by future progression-decision logic: a user who consistently
 * dismisses suggestions on a lift is signalling their RPE shouldn't
 * drive load reductions there — we should weight their RPE less
 * heavily as a fatigue signal for that lift.
 *
 * Returns null if there's no data to score against (don't penalize
 * users for low-data exercises).
 */
export async function getGateAcceptanceRate(
  exerciseName: string,
  lookback: number = 10,
): Promise<{ proposals: number; accepted: number; rate: number } | null> {
  try {
    const sessions = await getSessionHistory({ limit: lookback });
    let proposals = 0;
    let accepted = 0;
    const lower = exerciseName.toLowerCase();
    for (const session of sessions) {
      const pt = session as PTSessionLog;
      if (!pt.gateInteractions || pt.gateInteractions.length === 0) continue;
      for (const interaction of pt.gateInteractions) {
        if (interaction.exerciseName.toLowerCase() !== lower) continue;
        // Only count actionable proposals — 'stop' is informational.
        if (interaction.action === 'stop') continue;
        proposals++;
        if (interaction.accepted) accepted++;
      }
    }
    if (proposals === 0) return null;
    return { proposals, accepted, rate: accepted / proposals };
  } catch (err) {
    console.warn('getGateAcceptanceRate failed:', err);
    return null;
  }
}

/**
 * Clear all progression decisions (e.g., after deload week)
 */
export async function clearProgressionDecisions(): Promise<void> {
  try {
    const userId = getCurrentUserId();
    if (!userId) return;

    await db.autoProgressDecisions.where('userId').equals(userId).delete();
    console.log('� Cleared all progression decisions');
  } catch (error) {
    console.error('Failed to clear progression decisions:', error);
  }
}

